import { useRef, useCallback, useEffect } from "react";

export const useInfiniteScroll = (
	isFetching: boolean,
	hasNextPage: boolean,
	fetchNextPage: () => void,
	scrollRootId?: string,
) => {
	const observer = useRef<IntersectionObserver | null>(null);
	const calledRef = useRef(false);
	const scrollListenerRef = useRef<(() => void) | null>(null);

	// Reset calledRef when fetching is complete
	useEffect(() => {
		if (!isFetching) {
			calledRef.current = false;
		}
	}, [isFetching]);

	// Cleanup function
	useEffect(() => {
		return () => {
			if (observer.current) {
				observer.current.disconnect();
			}
			if (scrollListenerRef.current) {
				window.removeEventListener("scroll", scrollListenerRef.current);
			}
		};
	}, []);

	const lastElementRef = useCallback(
		(node: HTMLElement | null) => {
			if (observer.current) observer.current.disconnect();
			if (scrollListenerRef.current) {
				window.removeEventListener("scroll", scrollListenerRef.current);
			}

			const rootEl = scrollRootId ? document.getElementById(scrollRootId) : null;

			// Intersection Observer approach
			observer.current = new IntersectionObserver(
				entries => {
					const entry = entries[0];

					const shouldFetch =
						entry.isIntersecting && entry.intersectionRatio > 0 && hasNextPage && !isFetching && !calledRef.current;

					console.log("useInfiniteScroll - Intersection observer triggered:", {
						isIntersecting: entry.isIntersecting,
						intersectionRatio: entry.intersectionRatio,
						hasNextPage,
						isFetching,
						calledRef: calledRef.current,
						shouldFetch,
					});

					if (shouldFetch) {
						console.log("useInfiniteScroll - Fetching next page");
						calledRef.current = true;
						fetchNextPage();
					}
				},
				{
					root: rootEl,
					rootMargin: "200px", // Increased from 100px to trigger earlier
					threshold: [0, 0.1, 0.5, 1.0], // Multiple thresholds for better detection
				},
			);

			// Fallback scroll listener
			const handleScroll = () => {
				if (!node || !hasNextPage || isFetching || calledRef.current) return;

				const rect = node.getBoundingClientRect();
				const isNearBottom = rect.top <= window.innerHeight + 300; // 300px threshold

				if (isNearBottom) {
					console.log("useInfiniteScroll - Scroll fallback triggered");
					calledRef.current = true;
					fetchNextPage();
				}
			};

			scrollListenerRef.current = handleScroll;
			window.addEventListener("scroll", handleScroll, { passive: true });

			if (node) {
				console.log("useInfiniteScroll - Observing node:", node);
				observer.current.observe(node);
			}
		},
		[isFetching, hasNextPage, fetchNextPage, scrollRootId],
	);

	return lastElementRef;
};
